import { SearchX } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { Link } from 'react-router-dom'
import { PageHero } from '../components/layout/PageHero'
import { buttonClasses } from '../components/ui/button-variants'
import { Container } from '../components/ui/Container'
import { EmptyState } from '../components/ui/EmptyState'
import { useDocumentMeta } from '../hooks/useDocumentMeta'
import { showAppSurfaces } from '../lib/appSurfaces'
import { routes } from '../lib/navigation'

export const NotFoundPage = () => {
  const { t } = useTranslation()

  useDocumentMeta({ title: t('notFound.metaTitle') })

  /*
   * ─── 🔴 13 OCT: THIS PAGE WAS SENDING PEOPLE TO THINGS THAT ARE NOT THERE ──
   *
   * It said *"Search from the homepage, or browse the categories to find the
   * right business."* A QA pass measured the homepage in the shipped state:
   * **0 input fields and 0 category links.** Both are app-backed surfaces and
   * `APP_SURFACES` is `'hidden'` (Docs/34), so the only real exits were the two
   * buttons below.
   *
   * ⚠️ A 404 IS THE ONE PAGE WHERE WRONG ADVICE IS CERTAIN TO BE FOLLOWED —
   * everybody who reads it is already lost. Offering two routes that do not
   * exist turns a recoverable dead end into a second one.
   *
   * Branched on the constant rather than fixed one way, because the original
   * copy is CORRECT when the surfaces are on and will be right again the day
   * they are. ⚠️ **The constant goes FIRST in any `&&`** — CLAUDE.md § 34: with
   * a runtime value first, Rollup cannot fold the branch and the unused string
   * ships. Here it is the whole condition, so the dead branch is dropped.
   */
  const emptyBodyKey = showAppSurfaces() ? 'notFound.emptyBody' : 'notFound.emptyBodyHidden'

  return (
    <>
      {/*
        A 404 still gets a real <h1> and the full page furniture. The SEO
        checklist in Docs/07 §9 applies to every route, not only the good ones.
      */}
      <PageHero eyebrow={t('notFound.code')} title={t('notFound.title')} description={t('notFound.body')} />

      <Container className="py-12 lg:py-16">
        <EmptyState
          icon={SearchX}
          title={t('notFound.emptyTitle')}
          description={t(emptyBodyKey)}
          action={
            <div className="flex flex-col gap-3 sm:flex-row">
              <Link to={routes.home} className={buttonClasses('primary', 'md')}>
                {t('notFound.backHome')}
              </Link>
              <Link to={routes.support} className={buttonClasses('outline', 'md')}>
                {t('notFound.contactSupport')}
              </Link>
            </div>
          }
        />
      </Container>
    </>
  )
}
